import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  ArrowLeft, Calendar, List, ChevronLeft, ChevronRight, MapPin, Users, Clock,
  Loader2, Search, Filter, Building2, User, Phone, Mail, AlertTriangle,
  CheckCircle, XCircle, Star, Rocket, RefreshCw, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDateShort, formatDateFull } from '../../utils/timeFormat';
import QuickFillModal from '../../components/QuickFillModal';
import { ReliabilityBadge, CommitmentBadge } from '../../components/ReliabilityBadge';
import StatusChip from '../../components/StatusChip';

// Status color mapping
const STATUS_COLORS = {
  filled: 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-green-200',
  partially_filled: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-200',
  open: 'bg-gradient-to-r from-orange-400 to-amber-400 text-white shadow-lg shadow-orange-200',
  premium: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-200',
  urgent: 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-200',
  cancelled: 'bg-gradient-to-r from-gray-400 to-slate-400 text-white shadow-md'
};

// Local-date YYYY-MM-DD without UTC drift. `toISOString()` shifts backwards
// in negative UTC timezones, so we build the string from local components.
const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Iter-341 — Normalize any date-ish string the API might return
// (`'2026-06-15'`, `'2026-06-15T00:00:00'`, `'2026-06-15 00:00:00Z'`) to a
// canonical `YYYY-MM-DD`. Returns null when the input can't be parsed so
// callers can `continue`/`return false` instead of silently mis-rendering.
const normalizeDateStr = (v) => {
  if (typeof v !== 'string' || v.length < 10) return null;
  const head = v.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
};

const STATUS_DOT_COLORS = {
  filled: 'bg-gradient-to-r from-emerald-500 to-green-500',
  partially_filled: 'bg-gradient-to-r from-blue-500 to-cyan-500',
  open: 'bg-gradient-to-r from-orange-400 to-amber-400',
  premium: 'bg-gradient-to-r from-purple-500 to-pink-500',
  urgent: 'bg-gradient-to-r from-red-500 to-rose-500',
  cancelled: 'bg-gradient-to-r from-gray-400 to-slate-400'
};

const STATUS_LABELS = {
  filled: 'Fully Staffed',
  partially_filled: 'Partially Staffed',
  open: 'Open - Needs Staff',
  premium: 'Premium / VIP',
  urgent: 'Urgent',
  cancelled: 'Cancelled'
};

const AdminCalendar = () => {
  const navigate = useNavigate();
  // Default to List view on small screens (calendar grid is too dense for phones)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'list';
    return 'calendar';
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    client_id: '',
    role_id: '',
    status: '',
    city: '',
    state: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [dayDrawerDate, setDayDrawerDate] = useState(null); // { date: 'YYYY-MM-DD', bookings: [...] }
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Quick Fill Modal state
  const [quickFillOpen, setQuickFillOpen] = useState(false);
  const [quickFillShiftId, setQuickFillShiftId] = useState(null);

  // Fetch calendar stats
  const fetchStats = useCallback(async () => {
    try {
      // Iter-371 — scope the stats chips to the visible month so the
      // "Unstaffed 12" tile matches what the calendar renders when
      // the user picks the Open/Staffing Needed filter. Prior chip
      // was open-ended future → operators saw "12 unstaffed" on Aug
      // but an empty Aug calendar because those 12 lived in Sep+.
      const firstDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
      const lastDay  = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
      const params = new URLSearchParams();
      params.append('start_date', toLocalDateStr(firstDay));
      params.append('end_date',   toLocalDateStr(lastDay));
      const res = await api.get(`/admin/calendar/stats?${params.toString()}`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [selectedMonth]);

  // Fetch filter options
  const fetchFilters = useCallback(async () => {
    try {
      const res = await api.get('/admin/calendar/filters');
      setFilters(res.data);
    } catch (err) {
      console.error('Failed to fetch filters:', err);
    }
  }, []);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Date range for current view — use local date string (NOT toISOString,
      // which shifts to UTC and can land on previous day in negative offsets).
      const firstDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
      const lastDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
      params.append('start_date', toLocalDateStr(firstDay));
      params.append('end_date', toLocalDateStr(lastDay));
      
      // Apply filters
      if (selectedFilters.client_id) params.append('client_id', selectedFilters.client_id);
      if (selectedFilters.role_id) params.append('role_id', selectedFilters.role_id);
      if (selectedFilters.status) params.append('status', selectedFilters.status);
      if (selectedFilters.city) params.append('city', selectedFilters.city);
      if (selectedFilters.state) params.append('state', selectedFilters.state);
      
      const res = await api.get(`/admin/calendar/bookings?${params.toString()}`);
      setBookings(res.data.bookings || []);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedFilters]);

  // Fetch booking detail
  const fetchBookingDetail = async (shiftId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/calendar/bookings/${shiftId}`);
      setBookingDetail(res.data);
    } catch (err) {
      toast.error('Failed to load booking details');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchStats();
  }, [fetchFilters, fetchStats]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // P0-2026-09 — Refresh the calendar every time the tab/window regains
  // focus and every time the user navigates back to this route. This
  // fixes "I created a job in another tab / on my phone but the calendar
  // isn't showing it yet" without adding a websocket dependency.
  useEffect(() => {
    const onFocus = () => { fetchBookings(); fetchStats(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchBookings();
        fetchStats();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchBookings, fetchStats]);

  // Handle booking click
  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    fetchBookingDetail(booking.id);
  };

  // Handle full day-cell click — cross-platform (must still work with event dots/cards)
  const handleDayClick = (dateStr, dayBookings) => {
    if (dayBookings && dayBookings.length > 0) {
      // Day has shifts — open the Day Drawer with list of shifts
      setDayDrawerDate({ date: dateStr, bookings: dayBookings });
    } else {
      // Empty day — launch Create Shift with the date pre-filled
      navigate('/admin/shifts/create', { state: { prefill: { shift_date: dateStr } } });
    }
  };

  // Update booking status
  const handleStatusUpdate = async (shiftId, newStatus) => {
    try {
      await api.patch(`/admin/calendar/bookings/${shiftId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchBookings();
      if (selectedBooking?.id === shiftId) {
        fetchBookingDetail(shiftId);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Format time display
  const formatTime = (time) => {
    if (!time) return 'TBD';  // Iter-336 — TBD-aware display
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  // Filter bookings by search
  const filteredBookings = bookings.filter(b => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      b.venue_name?.toLowerCase().includes(search) ||
      b.role_name?.toLowerCase().includes(search) ||
      b.client_name?.toLowerCase().includes(search) ||
      b.company_name?.toLowerCase().includes(search) ||
      b.city?.toLowerCase().includes(search)
    );
  });

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    const firstDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const lastDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    const startPadding = firstDay.getDay();
    
    // Padding days
    for (let i = 0; i < startPadding; i++) {
      days.push(<div key={`pad-${i}`} className="min-h-[140px] bg-slate-50/30 rounded-xl" />);
    }
    
    // Calendar days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      const dateStr = toLocalDateStr(date);

      // Iter-335 + Iter-341 + Iter-343 — Admin calendar month-view guarantee.
      // A shift is ALWAYS rendered on its exact `shift_date`, and ONLY on
      // that date. Multi-day events are stored as one shift row per day
      // (the platform's canonical data model), so range-painting a single
      // row across multiple days would double-count. This filter enforces:
      // "shift renders on its shift_date only — no exceptions."
      //
      // If a client ever needs a genuine "event banner" across multiple
      // days, that's a separate rendering concern (event_group_id) — not
      // this per-shift day-cell loop.
      // Iter-341 — normalize date strings before comparing so an ISO
      // datetime (`'2026-06-15T00:00:00'`) doesn't silently drop the row.
      const dayBookings = filteredBookings.filter(b => {
        const startStr = normalizeDateStr(b.shift_date);
        if (!startStr) return false;
        return dateStr === startStr;
      });
      
      const isToday = dateStr === toLocalDateStr(new Date());
      const isPast = date < new Date(new Date().setHours(0,0,0,0));

      days.push(
        <div
          key={day}
          role="button"
          tabIndex={0}
          className={`min-h-[72px] sm:min-h-[140px] rounded-lg sm:rounded-xl transition-all duration-200 cursor-pointer active:scale-[0.98] ${
            isToday
              ? 'ring-2 ring-cyan-500 ring-offset-1 sm:ring-offset-2 bg-gradient-to-b from-cyan-50 to-white shadow-lg'
              : isPast
                ? 'bg-slate-50/50 opacity-60 hover:opacity-100'
                : 'bg-white hover:shadow-lg hover:scale-[1.01] border border-slate-100'
          }`}
          onClick={() => handleDayClick(dateStr, dayBookings)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDayClick(dateStr, dayBookings); }}
          data-testid={`calendar-day-${day}`}
          title={dayBookings.length > 0 ? `${dayBookings.length} booking(s) — tap to view` : 'Tap to create a shift on this day'}
        >
          {/* Day header */}
          <div className={`flex items-center justify-between px-1.5 sm:px-2.5 py-1 sm:py-2 ${
            isToday ? 'bg-cyan-500' : dayBookings.length > 0 ? 'bg-slate-100' : ''
          } rounded-t-lg sm:rounded-t-xl`}>
            <span className={`text-xs sm:text-sm font-bold ${
              isToday ? 'text-white' : isPast ? 'text-slate-400' : 'text-slate-700'
            }`}>
              {day}
            </span>
            {isToday && (
              <span className="text-[9px] sm:text-[10px] bg-white text-cyan-600 px-1 sm:px-2 py-0.5 rounded-full font-bold shadow-sm">
                TODAY
              </span>
            )}
            {dayBookings.length > 0 && !isToday && (
              <span className="text-[9px] sm:text-[10px] bg-cyan-500 text-white px-1 sm:px-2 py-0.5 rounded-full font-bold shadow-sm">
                {dayBookings.length}
              </span>
            )}
          </div>

          {/* Bookings — tiny dots on mobile, full cards on desktop */}
          <div className="p-1 sm:p-1.5 space-y-1 sm:space-y-1.5 overflow-y-auto max-h-[40px] sm:max-h-[100px] scrollbar-thin">
            {/* Mobile: show colored dots only (tappable still opens detail) */}
            <div className="sm:hidden flex flex-wrap gap-0.5">
              {dayBookings.slice(0, 6).map(booking => (
                <button
                  key={booking.id}
                  className={`h-2 w-2 rounded-full ${STATUS_COLORS[booking.staffing_status]}`}
                  onClick={(e) => { e.stopPropagation(); handleBookingClick(booking); }}
                  aria-label={booking.venue_name || booking.role_name}
                  data-testid={`booking-dot-${booking.id}`}
                />
              ))}
              {dayBookings.length > 6 && (
                <span className="text-[8px] text-slate-500 ml-0.5">+{dayBookings.length - 6}</span>
              )}
            </div>
            {/* Desktop: full cards */}
            {dayBookings.slice(0, 3).map(booking => (
              <div
                key={booking.id}
                className={`hidden sm:block text-[11px] px-2.5 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.03] hover:-translate-y-0.5 ${STATUS_COLORS[booking.staffing_status]}`}
                onClick={(e) => { e.stopPropagation(); handleBookingClick(booking); }}
                data-testid={`booking-${booking.id}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold">{formatTime(booking.start_time)}</span>
                  <div className="flex items-center gap-1">
                    {booking.duration_days > 1 && (
                      <span className="text-[9px] bg-white/30 px-1 rounded">{booking.duration_days}d</span>
                    )}
                    {booking.is_premium && <Star className="w-3 h-3 flex-shrink-0" />}
                    {booking.staffing_status === 'urgent' && <AlertTriangle className="w-3 h-3 flex-shrink-0 animate-pulse" />}
                  </div>
                </div>
                <div className="truncate font-semibold">{booking.venue_name || booking.role_name}</div>
                <div className="truncate text-[10px] opacity-80">
                  {booking.confirmed_count}/{booking.talent_needed} staff
                  {(booking.backups_needed || 0) > 0 && (
                    <span className="ml-1 px-1 rounded bg-amber-200 text-amber-900 text-[9px] font-bold">
                      +{booking.backup_count || 0}B
                    </span>
                  )}
                </div>
              </div>
            ))}
            {dayBookings.length > 3 && (
              <button
                className="hidden sm:block w-full text-[10px] text-center py-1.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 rounded-lg text-slate-600 font-bold transition-all shadow-sm"
                onClick={(e) => { e.stopPropagation(); handleDayClick(dateStr, dayBookings); }}
              >
                +{dayBookings.length - 3} more
              </button>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 shrink-0 px-2"
                onClick={() => navigate('/admin/home')}
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">Operations Calendar</h1>
                <p className="text-cyan-300 text-xs sm:text-sm hidden sm:block">Platform-wide booking management</p>
              </div>
            </div>

            {/* Quick Stats — Iter-371: chips are now scoped to the visible
                month AND clickable. Clicking a chip applies the matching
                status filter so the count and the calendar cards stay in
                sync. The tooltip name-drops the month for clarity
                ("Unstaffed in Aug 2026" vs "Unstaffed all-time"). */}
            {stats && (
              <div className="grid grid-cols-4 md:flex md:items-center gap-2 md:gap-4 w-full md:w-auto">
                <div className="text-center px-1 md:px-4 md:border-r md:border-white/20">
                  <p className="text-lg md:text-2xl font-bold" data-testid="stat-today">{stats.today_shifts}</p>
                  <p className="text-[10px] md:text-xs text-cyan-300">Today</p>
                </div>
                <div className="text-center px-1 md:px-4 md:border-r md:border-white/20">
                  <p className="text-lg md:text-2xl font-bold" data-testid="stat-week">{stats.this_week_shifts}</p>
                  <p className="text-[10px] md:text-xs text-cyan-300">Week</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFilters(f => ({ ...f, status: 'open' }))}
                  className={`text-center px-1 md:px-4 md:border-r md:border-white/20 hover:bg-white/10 rounded transition ${selectedFilters.status === 'open' ? 'ring-2 ring-orange-400 bg-white/10' : ''}`}
                  data-testid="stat-unstaffed-chip"
                  title={`${stats.unstaffed_shifts} shifts with zero confirmed talent in ${selectedMonth.toLocaleString('en-US',{month:'short',year:'numeric'})}`}
                >
                  <p className="text-lg md:text-2xl font-bold text-orange-400" data-testid="stat-unstaffed">{stats.unstaffed_shifts}</p>
                  <p className="text-[10px] md:text-xs text-cyan-300">
                    Unstaffed <span className="hidden md:inline">in {selectedMonth.toLocaleString('en-US',{month:'short'})}</span>
                  </p>
                </button>
                <div className="text-center px-1 md:px-4">
                  <p className="text-lg md:text-2xl font-bold text-red-400" data-testid="stat-urgent">{stats.urgent_shifts}</p>
                  <p className="text-[10px] md:text-xs text-cyan-300">Urgent</p>
                </div>
              </div>
            )}

            {/* View Toggle */}
            <div className="flex items-center gap-1.5 sm:gap-2 self-end md:self-auto">
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className={viewMode === 'calendar' ? 'bg-cyan-500 hover:bg-cyan-600 px-2 sm:px-3' : 'border-white/30 text-white hover:bg-white/10 px-2 sm:px-3'}
                data-testid="calendar-view-btn"
              >
                <Calendar className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-cyan-500 hover:bg-cyan-600 px-2 sm:px-3' : 'border-white/30 text-white hover:bg-white/10 px-2 sm:px-3'}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { fetchBookings(); fetchStats(); }}
                className="text-white hover:bg-white/10 px-2"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] sm:max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search venue, role, client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>

            {/* Filter Selects */}
            {filters && (
              <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
                <Select
                  value={selectedFilters.status}
                  onValueChange={(v) => setSelectedFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="partially_filled">Partially Filled</SelectItem>
                    <SelectItem value="filled">Filled</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={selectedFilters.client_id}
                  onValueChange={(v) => setSelectedFilters(f => ({ ...f, client_id: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="client-filter">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {filters.clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedFilters.role_id}
                  onValueChange={(v) => setSelectedFilters(f => ({ ...f, role_id: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="role-filter">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {filters.roles.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedFilters.state}
                  onValueChange={(v) => setSelectedFilters(f => ({ ...f, state: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full sm:w-[120px]" data-testid="state-filter">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {filters.states.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Clear Filters */}
            {(selectedFilters.status || selectedFilters.client_id || selectedFilters.role_id || selectedFilters.state) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFilters({ client_id: '', role_id: '', status: '', city: '', state: '' })}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-4 sm:py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <div className="bg-white rounded-xl sm:rounded-2xl border-0 shadow-xl overflow-hidden" data-testid="calendar-view">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-3 sm:p-5 bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hover:text-cyan-300 transition-all border border-white/20 px-2 sm:px-3"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
              >
                <ChevronLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <div className="text-center min-w-0 px-2">
                <h2 className="text-base sm:text-2xl font-bold text-white tracking-tight truncate">
                  {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <p className="text-xs sm:text-sm text-cyan-300 font-medium">
                  {filteredBookings.length} bookings
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hover:text-cyan-300 transition-all border border-white/20 px-2 sm:px-3"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4 sm:ml-1" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="p-2 sm:p-4 bg-gradient-to-b from-gray-50/50 to-white">
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {/* Day headers */}
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                  <div key={idx} className="text-center text-[10px] sm:text-xs font-bold text-slate-500 py-2 sm:py-3 tracking-wider">
                    <span className="sm:hidden">{day}</span>
                    <span className="hidden sm:inline">{['SUN','MON','TUE','WED','THU','FRI','SAT'][idx]}</span>
                  </div>
                ))}
                {generateCalendarDays()}
              </div>
            </div>

            {/* Legend */}
            <div className="border-t border-slate-200 px-6 py-4 bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-b-2xl">
              <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
                {Object.entries(STATUS_LABELS).map(([status, label]) => (
                  <div key={status} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                    <span className={`w-4 h-4 rounded-full ${STATUS_DOT_COLORS[status]} shadow-inner`}></span>
                    <span className="text-slate-700 font-semibold">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="space-y-3" data-testid="list-view">
            {filteredBookings.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-40" />
                <p>No bookings found for this period</p>
              </div>
            ) : (
              filteredBookings.map(booking => (
                <Card
                  key={booking.id}
                  className="hover:shadow-lg transition-all cursor-pointer border-l-4"
                  style={{
                    borderLeftColor: booking.staffing_status === 'filled' ? '#10b981' :
                      booking.staffing_status === 'partially_filled' ? '#3b82f6' :
                      booking.staffing_status === 'urgent' ? '#ef4444' :
                      booking.staffing_status === 'premium' ? '#a855f7' :
                      booking.staffing_status === 'cancelled' ? '#6b7280' : '#f59e0b'
                  }}
                  onClick={() => navigate(`/admin/shifts/${booking.id}`)}
                  data-testid={`list-booking-${booking.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">{booking.venue_name || booking.role_name}</h3>
                          <Badge className={STATUS_COLORS[booking.staffing_status]}>
                            {STATUS_LABELS[booking.staffing_status]}
                          </Badge>
                          {booking.is_premium && (
                            <Badge className="bg-purple-100 text-purple-700">
                              <Star className="w-3 h-3 mr-1" />
                              VIP
                            </Badge>
                          )}
                          {booking.duration_days > 1 && (
                            <Badge variant="outline">
                              {booking.duration_days} days
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDateShort(booking.shift_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {booking.city}, {booking.state}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {booking.company_name || booking.client_name}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end mb-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="font-bold text-lg">
                            {booking.confirmed_count}/{booking.talent_needed}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{booking.role_name}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {/* Day Drawer — opens when admin taps a calendar day cell */}
      <Dialog open={!!dayDrawerDate} onOpenChange={() => setDayDrawerDate(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
              <span>
                {dayDrawerDate?.date && new Date(dayDrawerDate.date + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                })}
              </span>
              <Badge variant="outline" className="text-xs">
                {dayDrawerDate?.bookings?.length || 0} shift{dayDrawerDate?.bookings?.length === 1 ? '' : 's'}
              </Badge>
            </DialogTitle>
          <DialogDescription className="sr-only">Shifts for the selected day</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {(dayDrawerDate?.bookings || []).map((b) => (
              <button
                key={b.id}
                className={`w-full text-left p-3 rounded-lg border transition hover:shadow-md ${STATUS_COLORS[b.staffing_status] || 'bg-white border-slate-200'}`}
                onClick={() => {
                  setDayDrawerDate(null);
                  handleBookingClick(b);
                }}
                data-testid={`day-drawer-shift-${b.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold">{formatTime(b.start_time)} — {formatTime(b.end_time)}</span>
                  <span className="text-xs font-semibold">{b.confirmed_count}/{b.talent_needed} staffed</span>
                </div>
                <div className="text-sm font-semibold truncate">{b.venue_name || b.role_name}</div>
                <div className="text-xs opacity-80 truncate">
                  {b.company_name} · {b.city}, {b.state}
                </div>
              </button>
            ))}
          </div>
          <div className="pt-3 border-t mt-3 flex flex-col sm:flex-row gap-2">
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 text-white w-full sm:w-auto"
              onClick={() => {
                const d = dayDrawerDate?.date;
                setDayDrawerDate(null);
                navigate('/admin/shifts/create', { state: { prefill: { shift_date: d } } });
              }}
              data-testid="day-drawer-create-shift"
            >
              + Create shift on this day
            </Button>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setDayDrawerDate(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedBooking} onOpenChange={() => { setSelectedBooking(null); setBookingDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Booking Details</span>
              {bookingDetail?.shift?.staffing_status && (
                <Badge className={STATUS_COLORS[bookingDetail.shift.staffing_status]}>
                  {STATUS_LABELS[bookingDetail.shift.staffing_status]}
                </Badge>
              )}
            </DialogTitle>
          <DialogDescription className="sr-only">Booking details</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : bookingDetail ? (
            <div className="space-y-6">
              {/* Event Info */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-lg">{bookingDetail.shift.venue_name || bookingDetail.shift.role_name}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{formatDateFull(bookingDetail.shift.shift_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{formatTime(bookingDetail.shift.start_time)} - {formatTime(bookingDetail.shift.end_time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{bookingDetail.shift.city}, {bookingDetail.shift.state} {bookingDetail.shift.zip_code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{bookingDetail.shift.role_name}</span>
                  </div>
                </div>
              </div>

              {/* Staffing Status */}
              <div className="bg-white border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Staffing ({bookingDetail.shift.confirmed_count}/{bookingDetail.shift.talent_needed})
                  </h4>
                  {bookingDetail.shift.staffing_gap > 0 && (
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                      onClick={() => {
                        setQuickFillShiftId(bookingDetail.shift.shift_id);
                        setQuickFillOpen(true);
                      }}
                      data-testid="quick-fill-btn"
                    >
                      <Rocket className="w-4 h-4 mr-1" />
                      Quick Fill
                    </Button>
                  )}
                </div>
                {bookingDetail.shift.staffing_gap > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <p className="text-orange-700 text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {bookingDetail.shift.staffing_gap} more staff needed
                    </p>
                  </div>
                )}

                {/* Assigned Talent */}
                {bookingDetail.assigned_talent.length > 0 ? (
                  <div className="space-y-2">
                    {bookingDetail.assigned_talent.map(talent => (
                      <div key={talent.booking_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={talent.profile_photo} />
                            <AvatarFallback>{talent.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{talent.name}</p>
                            <p className="text-xs text-gray-500">{talent.city}, {talent.state}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusChip status={talent.booking_status} />
                          {talent.phone && (
                            <a href={`tel:${talent.phone}`} className="text-gray-400 hover:text-cyan-500">
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                          {talent.email && (
                            <a href={`mailto:${talent.email}`} className="text-gray-400 hover:text-cyan-500">
                              <Mail className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No talent assigned yet</p>
                )}
              </div>

              {/* Client Info */}
              <div className="bg-white border rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Client
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{bookingDetail.shift.company_name || bookingDetail.shift.client_name}</p>
                  <p className="text-gray-600 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {bookingDetail.shift.client_name}
                  </p>
                  {bookingDetail.shift.client_email && (
                    <p className="text-gray-600 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {bookingDetail.shift.client_email}
                    </p>
                  )}
                  {bookingDetail.shift.client_phone && (
                    <p className="text-gray-600 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {bookingDetail.shift.client_phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label>Update Status:</Label>
                  <Select
                    value={bookingDetail.shift.status}
                    onValueChange={(v) => handleStatusUpdate(bookingDetail.shift.id, v)}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="filled">Filled</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/shifts/${bookingDetail.shift.id}`)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Full Details
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Quick Fill Modal */}
      <QuickFillModal
        isOpen={quickFillOpen}
        onClose={() => {
          setQuickFillOpen(false);
          setQuickFillShiftId(null);
        }}
        shiftId={quickFillShiftId}
        onTalentSelected={(talentIds) => {
          // Refresh the booking detail
          if (selectedBooking) {
            fetchBookingDetail(selectedBooking);
          }
          fetchBookings();
        }}
      />
    </div>
  );
};

export default AdminCalendar;
